import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import DynamicTable from "../../../../unicomponents/Tables/DynamicTable";
import DropdownSchoolYear from "../../../../unicomponents/Dropdowns/DropdownSchoolYear";
import DropdownUniqueItems from "../../../../unicomponents/Dropdowns/DropdownUniqueItems";
import useOrganizationSetting from "../../../../hooks/useOrganizationSetting";
import useRegistrationInfo from "../../../../hooks/useRegistrationInfo";
import useTermPeriod from "../../../../hooks/useTermPeriod";
import useFlexibleData from "../../../../hooks/useFlexibleData";
import useTransferStudent from "../../../../hooks/useTransferStudent";
import { useUIContext } from "../../../../context/UIContext";
import { generateAllRegisteredStudents } from "../../../../util/dealingArrObj";
import { gradeToConvert100 } from "../../../../util/specialCase";

export default function TransferStudent() {
  const { t } = useTranslation();
  const { handleToastCenterTop, setToastConfirmModalState } = useUIContext();
  const location = useLocation();
  const paths = location.pathname.split("/");
  const schoolId = paths[1];

  // 필터 상태
  const [filters, setFilters] = useState({
    selectedStudent: "",
    schoolYear: null,
    schoolYearId: "",
    termId: "",
    termName: "",
  });

  // 학생 검색 관련 상태
  const [searchKey, setSearchKey] = useState("");
  const [studentList, setStudentList] = useState([]);
  const [usernameList, setUsernameList] = useState([]);

  // 전학생 데이터 상태
  const [studentInfo, setStudentInfo] = useState({
    schoolYear: "",
    semester: "",
    grade: "",
    recordType: "",
    schoolName: "",
  });

  // 과목 추가 폼 상태
  const [subjectForm, setSubjectForm] = useState({
    subjectName: "",
    subjectCategory: "",
    units: "",
    order: "",
    grade: "",
    convert100: "",
    average: "",
    convertedScore: "",
    ef: "",
  });

  // 저장된 전학생 데이터 상태
  const [savedTransferData, setSavedTransferData] = useState({});
  const [transferStudentData, setTransferStudentData] = useState([]);

  const {
    settingQuery: { data: settingData },
  } = useOrganizationSetting({ schoolid: schoolId });

  const {
    registrationInfoQuery: { data: registrationInfoData },
  } = useRegistrationInfo({ schoolid: schoolId }, { registrationInfo: true });

  const {
    termPeriodQuery: { data: termPeriodData },
  } = useTermPeriod({ schoolid: schoolId });

  const {
    flexibleDataQuery: { data: flexibleData },
    createOrUpdateFlexibleData,
    deleteFlexibleData,
  } = useFlexibleData({
    schoolid: schoolId,
    main_category: "transferStudent",
  });

  const {
    flexibleDataQuery: { data: flexibleDataConvert100 },
  } = useFlexibleData({
    schoolid: schoolId,
    main_category: "convert100",
    sub_category: "직접입력",
  });

  const { createTransferStudent, deleteTransferStudent, findTransferStudent } =
    useTransferStudent();

  const findTransferStudentRef = useRef(findTransferStudent);

  // savedTransferData.id가 변경될 때 전학생 과목 데이터 로드
  useEffect(() => {
    if (savedTransferData.id) {
      const conditions = {
        schoolid: schoolId,
        flexible_data_id: savedTransferData.id,
      };

      findTransferStudentRef.current.mutate(conditions, {
        onSuccess: (data) => {
          setTransferStudentData(data || []);
        },
        onError: (error) => {
          console.error("전학생 과목 데이터 로딩 에러:", error);
          setTransferStudentData([]);
        },
      });
    } else {
      setTransferStudentData([]);
    }
  }, [savedTransferData.id, schoolId]);

  // 학생 리스트 초기화 (Transcript.jsx 로직 참고)
  useEffect(() => {
    if (settingData && settingData.length > 0 && registrationInfoData) {
      // 전체 학생 리스트 생성
      let finalArray = generateAllRegisteredStudents(
        registrationInfoData,
        settingData
      );

      // 전체 학생 리스트 설정 (관리자 전용)
      setStudentList(finalArray);
    }
  }, [registrationInfoData, settingData]);

  // 학년도 초기화 (PrincipalComment.jsx 방식)
  useEffect(() => {
    let initialSchoolYear = null;
    let initialSchoolYearId = null;

    if (filters.schoolYear === null && settingData && settingData.length > 0) {
      const initialData = settingData.sort(
        (a, b) => b.school_year - a.school_year
      )[0];

      initialSchoolYear = initialData.school_year;
      initialSchoolYearId = initialData.id;

      setFilters((prev) => ({
        ...prev,
        schoolYear: initialSchoolYear,
        schoolYearId: initialSchoolYearId,
      }));

      setStudentInfo((prev) => ({
        ...prev,
        schoolYear: initialSchoolYear,
      }));
    }
  }, [filters.schoolYear, settingData]);

  // 저장된 전학생 데이터 로드 및 필드 업데이트
  useEffect(() => {
    // 학생이 선택되지 않았을 때는 모든 필드 초기화
    if (!filters.selectedStudent) {
      setSavedTransferData({});
      setStudentInfo((prev) => ({
        ...prev,
        recordType: "",
        schoolName: "",
        grade: "",
      }));
      return;
    }

    // 학년도, 학기, 학생이 모두 선택되었을 때 데이터 검색
    if (
      flexibleData &&
      filters.selectedStudent &&
      filters.schoolYearId &&
      filters.termId
    ) {
      const foundData = flexibleData.find(
        (item) =>
          item.school_year_id.toString() === filters.schoolYearId.toString() &&
          item.sub_category === filters.selectedStudent &&
          item.term_id.toString() === filters.termId.toString()
      );

      if (foundData) {
        setSavedTransferData(foundData);
        // 저장된 데이터를 studentInfo에 반영
        setStudentInfo((prev) => ({
          ...prev,
          recordType: foundData.data_value?.gradeSystem,
          schoolName: foundData.data_value?.previousSchool || "",
          grade: foundData.data_value?.studentYear?.toString() || "",
        }));
      } else {
        // 데이터가 없으면 저장된 데이터는 초기화하고 필드는 빈칸으로
        setSavedTransferData({});
        setStudentInfo((prev) => ({
          ...prev,
          recordType: "",
          schoolName: "",
          grade: "",
        }));
      }
    } else if (filters.selectedStudent && filters.schoolYearId) {
      // 학생과 학년도는 선택되었지만 학기가 선택되지 않았을 때
      setSavedTransferData({});
      setStudentInfo((prev) => ({
        ...prev,
        recordType: "",
        schoolName: "",
        grade: "",
      }));
    }
  }, [
    flexibleData,
    filters.selectedStudent,
    filters.schoolYearId,
    filters.termId,
  ]);

  // 학생 검색 핸들러 (Transcript.jsx 로직 참고)
  const handleSearch = (e) => {
    const { value } = e.target;
    setSearchKey(value);
    if (value === "") {
      setUsernameList([]);
    } else if (studentList) {
      const filteredRows = studentList.filter((row) =>
        row.fullname.toLowerCase().includes(value.toLowerCase())
      );

      // 고유한 username을 기준으로 중복 제거
      const uniqueFilteredRows = Array.from(
        new Map(filteredRows.map((item) => [item.username, item])).values()
      );

      // 고유한 항목으로 setUsernameList 업데이트
      setUsernameList(uniqueFilteredRows);
    }
  };

  // 학생 아이디 선택 핸들러 (Transcript.jsx 로직 참고)
  const handleSelectUsername = (e) => {
    const { value } = e.target;
    setFilters((prev) => ({
      ...prev,
      selectedStudent: value,
    }));

    // 학생이 바뀔 때 관련 필드들 초기화
    setStudentInfo((prev) => ({
      ...prev,
      grade: "",
      recordType: "",
      schoolName: "",
    }));
  };

  // 학생 정보 입력 핸들러
  const handleStudentInfoChange = (e) => {
    const { name, value } = e.target;
    setStudentInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // 드롭다운 변경 핸들러 (PrincipalComment.jsx 방식)
  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    const selectedOptionText = e.target.selectedOptions[0].text;

    if (name === "0") {
      // DropdownSchoolYear 처리 - 학년도가 바뀌면 학기와 관련 필드들 초기화
      setFilters((prev) => ({
        ...prev,
        schoolYear: selectedOptionText,
        schoolYearId: value,
        termId: "",
        termName: "",
      }));

      setStudentInfo((prev) => ({
        ...prev,
        schoolYear: selectedOptionText,
        semester: "",
        grade: "",
        recordType: "",
        schoolName: "",
      }));
    }

    if (name === "termName") {
      // 학기 처리 - 학기가 바뀌면 관련 필드들 초기화
      setFilters((prev) => ({
        ...prev,
        termId: value, // ID를 termId에 저장
        termName: selectedOptionText, // 텍스트를 termName에 저장
      }));

      setStudentInfo((prev) => ({
        ...prev,
        semester: selectedOptionText, // 학기 이름을 저장
        grade: "",
        recordType: "",
        schoolName: "",
      }));
    }
  };

  // 학생 정보 저장 핸들러 (PrincipalComment.jsx 방식)
  const handleSaveStudentInfo = () => {
    if (!filters.selectedStudent) {
      handleToastCenterTop("학생을 선택해주세요.");
      return;
    }

    if (!filters.termId) {
      handleToastCenterTop("학기를 선택해주세요.");
      return;
    }

    if (!studentInfo.grade || !studentInfo.recordType) {
      handleToastCenterTop("학년, 성적유형은 필수 입력 사항입니다.");
      return;
    }

    // flexibleData 저장용 데이터 구조
    const dataToSave = {
      ...(savedTransferData.id ? { id: savedTransferData.id } : {}), // update를 위한 id
      schoolid: schoolId,
      school_year_id: filters.schoolYearId,
      main_category: "transferStudent",
      sub_category: filters.selectedStudent,
      term_id: filters.termId, // term_id에 실제 ID 값 저장
      data_value: {
        studentYear: parseInt(studentInfo.grade),
        termName: filters.termName, // 학기 이름(텍스트) 저장
        gradeSystem: studentInfo.recordType,
        previousSchool: studentInfo.schoolName.trim(),
      },
    };

    createOrUpdateFlexibleData.mutate(dataToSave, {
      onSuccess: (successMessage) => {
        console.log(successMessage);
        handleToastCenterTop(t("common.messages.savedSuccessfully"));
      },
      onError: (error) => {
        console.error(error);
        alert(t("common.messages.errorAlert") + error.message);
      },
    });
  };

  // 학생 정보 삭제 핸들러
  const handleDeleteStudentInfo = () => {
    if (!savedTransferData.id) {
      handleToastCenterTop("삭제할 데이터가 없습니다.");
      return;
    }

    // 삭제 확인 모달 표시
    setToastConfirmModalState({
      visible: true,
      message:
        "삭제하면 해당 학기에 저장된 전학생 과목 정보도 같이 삭제됩니다. 정말 삭제하시겠습니까?",
      onConfirm: () => {
        const dataToDelete = {
          id: savedTransferData.id,
          schoolid: schoolId,
          main_category: "transferStudent",
          term_id: filters.termId, // term_id에 실제 ID 값
        };

        deleteFlexibleData.mutate(dataToDelete, {
          onSuccess: () => {
            handleToastCenterTop("데이터가 삭제되었습니다.");
            setSavedTransferData({});
            setStudentInfo((prev) => ({
              ...prev,
              recordType: "",
              schoolName: "",
              grade: "",
            }));
          },
          onError: (error) => {
            console.error(error);
            alert(t("common.messages.errorAlert") + error.message);
          },
        });
      },
      onCancel: () => {
        // 취소 시 아무것도 하지 않음
      },
    });
  };

  // 과목 폼 입력 핸들러
  const handleSubjectFormChange = (e) => {
    const { name, value } = e.target;

    if (name === "subjectCategory") {
      setSubjectForm((prev) => ({
        ...prev,
        subjectCategory: value,
        grade: "",
        average: "",
        convertedScore: "",
        convert100: "",
        ef: "",
      }));
      return;
    }

    setSubjectForm((prev) => {
      const newForm = { ...prev, [name]: value };

      if (
        studentInfo.recordType === "other" &&
        name === "grade" &&
        flexibleDataConvert100
      ) {
        const convert100Data = flexibleDataConvert100[0];
        if (convert100Data) {
          const letterGrade = gradeToConvert100(
            value,
            convert100Data.data_value,
            "",
            null // lessonInfo?.extra_attributes?.subjectCategory 대신 null 사용
          );
          newForm.convert100 = letterGrade || "";
          newForm.ef = getEffort(letterGrade);
        }

        return newForm;
      }

      // Score와 Average가 입력되면 Converted Score 자동 계산
      if (
        (name === "grade" || name === "average") &&
        newForm.grade &&
        newForm.average
      ) {
        const grade = parseFloat(newForm.grade);
        const average = parseFloat(newForm.average);
        const convertedScore = parseFloat((grade / average).toFixed(2));
        const gradeMap = { A: "1", B: "2", C: "3", D: "4", F: "5" };

        // convertedScore에 따른 등급 계산
        let convert100 = "";
        if (convertedScore >= 1.1) {
          convert100 = "A";
        } else if (convertedScore >= 0.8) {
          convert100 = "B";
        } else if (convertedScore >= 0.6) {
          convert100 = "C";
        } else if (convertedScore >= 0.4) {
          convert100 = "D";
        } else {
          convert100 = "F";
        }

        if (!isNaN(grade) && !isNaN(average) && average > 0) {
          newForm.convertedScore = convertedScore.toFixed(2);
          newForm.convert100 = convert100;
          newForm.ef = gradeMap[convert100] || "";
        }
      }
      return newForm;
    });
  };

  const getEffort = (letterGrade) => {
    const grade = letterGrade?.toUpperCase()?.trim();

    switch (grade) {
      case "A+":
      case "A":
      case "A-":
        return "1";
      case "B+":
      case "B":
      case "B-":
        return "2";
      case "C+":
      case "C":
      case "C-":
        return "3";
      case "D+":
      case "D":
      case "D-":
        return "4";
      case "F":
        return "5";
      default:
        return ""; // 알 수 없는 grade인 경우 빈 문자열 반환
    }
  };

  // 과목 추가 핸들러 (서버로 전송)
  const handleAddSubject = () => {
    if (!subjectForm.subjectName || !subjectForm.subjectCategory) {
      handleToastCenterTop("과목명과 수업유형을 입력해주세요.");
      return;
    }

    if (!savedTransferData.id) {
      handleToastCenterTop("전학생 정보를 먼저 저장해주세요.");
      return;
    }

    const subjectData = {
      schoolid: schoolId,
      flexible_data_id: savedTransferData.id,
      username: filters.selectedStudent, // username 저장
      subject_name: subjectForm.subjectName,
      subject_category: subjectForm.subjectCategory,
      units: subjectForm.units,
      order: subjectForm.order,
      grade: subjectForm.grade,
      average: subjectForm.average,
      converted_score: subjectForm.convertedScore,
      convert100: subjectForm.convert100,
      ef: subjectForm.ef,
    };

    createTransferStudent.mutate(subjectData, {
      onSuccess: () => {
        handleToastCenterTop("과목이 추가되었습니다.");
        // 폼 초기화
        setSubjectForm({
          subjectName: "",
          subjectCategory: "",
          units: "",
          order: "",
          grade: "",
          convert100: "",
          average: "",
          convertedScore: "",
          ef: "",
        });
        // 데이터 다시 로드
        const conditions = {
          schoolid: schoolId,
          flexible_data_id: savedTransferData.id,
        };
        findTransferStudentRef.current.mutate(conditions, {
          onSuccess: (data) => {
            setTransferStudentData(data || []);
          },
        });
      },
      onError: (error) => {
        console.error("과목 추가 에러:", error);
        handleToastCenterTop("과목 추가에 실패했습니다.");
      },
    });
  };

  // 과목 삭제 핸들러 (서버에서 삭제)
  const handleDeleteSubject = (subjectId) => {
    setToastConfirmModalState({
      visible: true,
      message: "과목을 삭제하시겠습니까?",
      onConfirm: () => {
        deleteTransferStudent.mutate(subjectId, {
          onSuccess: () => {
            handleToastCenterTop("과목이 삭제되었습니다.");
            // 데이터 다시 로드
            const conditions = {
              schoolid: schoolId,
              flexible_data_id: savedTransferData.id,
            };
            findTransferStudentRef.current.mutate(conditions, {
              onSuccess: (data) => {
                setTransferStudentData(data || []);
              },
            });
          },
          onError: (error) => {
            console.error("과목 삭제 에러:", error);
            handleToastCenterTop("과목 삭제에 실패했습니다.");
          },
        });
      },
      onCancel: () => {
        // 취소 시 아무것도 하지 않음
      },
    });
  };

  // 옵션 데이터
  const recordTypeOptions = [
    { value: "korean_public", label: "한국공교육" },
    { value: "other", label: "그외" },
    { value: "notAvailable", label: "호환불가" },
  ];

  const gradeNumberOptions = [
    { value: "9", label: "9" },
    { value: "10", label: "10" },
    { value: "11", label: "11" },
    { value: "12", label: "12" },
  ];

  // 수업유형 옵션
  const subjectCategoryOptions = [
    { value: "general", label: "일반" },
    { value: "arts_pe", label: "예체능" },
  ];

  // 테이블 설정
  const tableConfig = {
    style: { fontSize: "14px" },
    headers: [
      { label: "순서", style: { width: "60px", textAlign: "center" } },
      { label: "과목명", style: { width: "150px", textAlign: "left" } },
      { label: "유형", style: { width: "80px", textAlign: "center" } },
      { label: "Units", style: { width: "70px", textAlign: "center" } },
      { label: "Score", style: { width: "70px", textAlign: "center" } },
      { label: "Average", style: { width: "70px", textAlign: "center" } },
      { label: "Converted", style: { width: "80px", textAlign: "center" } },
      { label: "Grade", style: { width: "60px", textAlign: "center" } },
      { label: "EF", style: { width: "50px", textAlign: "center" } },
      { label: "작업", style: { width: "100px", textAlign: "center" } },
    ],
    columns: [
      {
        valueKey: "order",
        cellStyle: { textAlign: "center" },
      },
      {
        valueKey: "subject_name",
        cellStyle: { textAlign: "left", paddingLeft: "8px" },
      },
      {
        valueKey: "subject_category",
        cellStyle: { textAlign: "center" },
        valueFormatter: (value) => {
          if (value === "arts_pe") return "예체능";
          if (value === "general") return "일반";
          if (value === "other") return "그외";
          return value;
        },
      },
      {
        valueKey: "units",
        cellStyle: { textAlign: "center" },
      },
      {
        valueKey: "grade",
        cellStyle: { textAlign: "center" },
      },
      {
        valueKey: "average",
        cellStyle: { textAlign: "center" },
      },
      {
        valueKey: "converted_score",
        cellStyle: { textAlign: "center" },
      },
      {
        valueKey: "convert100",
        cellStyle: { textAlign: "center" },
      },
      {
        valueKey: "ef",
        cellStyle: { textAlign: "center" },
      },
      {
        valueFormatter: (value, item) => (
          <div className="d-flex gap-1 justify-content-center">
            <button
              className="btn btn-danger btn-sm"
              onClick={() => handleDeleteSubject(item.id)}
              style={{ fontSize: "12px", padding: "2px 6px" }}
            >
              삭제
            </button>
          </div>
        ),
        cellStyle: { textAlign: "center" },
      },
    ],
    rows: transferStudentData || [],
  };

  return (
    <div className="p-4">
      {/* 헤더 */}
      <div className="row mb-4">
        <div className="col-auto">
          <h3 className="fw-bold">전학생 성적 관리</h3>
        </div>
      </div>

      {/* 학생 검색 및 정보 입력 섹션 */}
      <div className="row g-3 mb-4 align-items-end">
        {/* 학생 검색 */}
        <div className="col-md-2 p-0 pe-1 mt-0">
          <label className="form-label">학생 검색</label>
          <input
            type="text"
            className="form-control"
            placeholder={t("common.user.studentName") || "학생이름"}
            value={searchKey}
            onChange={handleSearch}
          />
        </div>
        <div className="col-md-2 p-0 pe-1 mt-0">
          <label className="form-label">학생 아이디</label>
          <select
            className="form-select"
            onChange={handleSelectUsername}
            value={filters.selectedStudent || ""}
          >
            <option value="">학생 선택</option>
            {usernameList &&
              usernameList.map((user) => (
                <option key={user.id} value={user.username}>
                  {user.username}
                </option>
              ))}
          </select>
        </div>

        {/* 학년도, 학기, 학년 */}
        <div className="col-md-1 p-0 pe-1 mt-0">
          <label className="form-label">학년도</label>
          <DropdownSchoolYear
            settingData={settingData}
            handleDropdownChange={handleDropdownChange}
            schoolYearId={filters.schoolYearId}
            allYears={false}
          />
        </div>
        <div className="col-md-1 p-0 pe-1 mt-0">
          <label className="form-label">학기</label>
          {termPeriodData && (
            <DropdownUniqueItems
              displayData={termPeriodData}
              displayKey="term_name"
              valueKey="id"
              uniqueKey="term_name"
              sortKey="term_name"
              isDesc={false}
              handleDropdownChange={handleDropdownChange}
              selectedItem={filters.termId}
              selectName="termName"
              dropdownTitle={t("common.labels.termName")}
            />
          )}
        </div>
        <div className="col-md-1 p-0 pe-1 mt-0">
          <label className="form-label">학년</label>
          <select
            className="form-select"
            name="grade"
            value={studentInfo.grade}
            onChange={handleStudentInfoChange}
          >
            <option value="">선택</option>
            {gradeNumberOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* 성적유형, 학교명, 버튼들 */}
        <div className="col-md-2 p-0 pe-1 mt-0">
          <label className="form-label">성적유형</label>
          <select
            className="form-select"
            name="recordType"
            value={studentInfo.recordType}
            onChange={handleStudentInfoChange}
          >
            <option value="">선택</option>
            {recordTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-2 p-0 pe-1 mt-0">
          <label className="form-label">학교명</label>
          <input
            type="text"
            className="form-control"
            name="schoolName"
            value={studentInfo.schoolName}
            onChange={handleStudentInfoChange}
            placeholder="학교명을 입력하세요"
          />
        </div>
        <div className="col-md-1 p-0 mt-0">
          <div className="d-flex mb-1">
            <button
              className="btn btn-primary btn-sm ps-1 pe-1 m-0 me-1"
              onClick={handleSaveStudentInfo}
            >
              저장
            </button>
            <button
              className="btn btn-danger btn-sm ps-1 pe-1  m-0"
              onClick={handleDeleteStudentInfo}
            >
              삭제
            </button>
          </div>
        </div>
      </div>

      {studentInfo.recordType === "notAvailable" ? (
        <div className="alert alert-warning mt-4">
          <h5>호환불가 성적유형입니다.</h5>
          <p>
            호환불가 성적유형은 과목을 입력할 수 없고, Transcript에 호환불가
            안내메시지가 나갑니다.
          </p>
        </div>
      ) : filters.selectedStudent && filters.schoolYearId && filters.termId ? (
        <>
          {/* 저장된 데이터가 있을 때만 과목 추가 섹션 표시 */}
          {savedTransferData.id ? (
            <>
              {/* 과목 추가 섹션 */}
              <div className="card mb-4">
                <div className="card-body">
                  <div className="d-flex gap-2">
                    <div className="" style={{ width: "65px" }}>
                      <label className="form-label">순서</label>
                      <input
                        type="number"
                        className="form-control form-control-sm p-1"
                        name="order"
                        value={subjectForm.order}
                        onChange={handleSubjectFormChange}
                      />
                    </div>
                    <div className="" style={{ minWidth: "200px" }}>
                      <label className="form-label">과목명</label>
                      <input
                        type="text"
                        className="form-control form-control-sm  p-1"
                        name="subjectName"
                        value={subjectForm.subjectName}
                        onChange={handleSubjectFormChange}
                        placeholder="과목명"
                      />
                    </div>
                    <div className="" style={{ width: "100px" }}>
                      <label className="form-label">유형</label>
                      <select
                        className="form-select form-select-sm  p-1"
                        name="subjectCategory"
                        value={subjectForm.subjectCategory}
                        onChange={handleSubjectFormChange}
                      >
                        <option value="">선택</option>
                        {studentInfo.recordType === "korean_public" ? (
                          subjectCategoryOptions.map((option) => (
                            <>
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            </>
                          ))
                        ) : (
                          <option value="other">그외</option>
                        )}
                      </select>
                    </div>
                    <div className="" style={{ width: "65px" }}>
                      <label className="form-label">Units</label>
                      <input
                        type="number"
                        className="form-control form-control-sm  p-1"
                        name="units"
                        value={subjectForm.units}
                        onChange={handleSubjectFormChange}
                      />
                    </div>
                    <div className="" style={{ width: "70px" }}>
                      <label className="form-label">Score</label>
                      <input
                        type="number"
                        className="form-control form-control-sm  p-1"
                        name="grade"
                        value={subjectForm.grade}
                        onChange={handleSubjectFormChange}
                        disabled={
                          subjectForm?.subjectCategory === "arts_pe"
                            ? true
                            : false
                        }
                      />
                    </div>
                    {studentInfo.recordType === "korean_public" && (
                      <>
                        <div className="" style={{ width: "70px" }}>
                          <label className="form-label">Average</label>
                          <input
                            type="number"
                            className="form-control form-control-sm  p-1"
                            name="average"
                            value={subjectForm.average}
                            onChange={handleSubjectFormChange}
                            disabled={
                              subjectForm?.subjectCategory === "arts_pe"
                                ? true
                                : false
                            }
                          />
                        </div>
                        <div className="" style={{ width: "75px" }}>
                          <label className="form-label">Converted</label>
                          <input
                            type="text"
                            className="form-control form-control-sm  p-1"
                            name="convertedScore"
                            value={subjectForm.convertedScore}
                            disabled={true}
                          />
                        </div>
                      </>
                    )}
                    <div className="" style={{ width: "60px" }}>
                      <label className="form-label">Grade</label>
                      <input
                        type="text"
                        className="form-control form-control-sm  p-1"
                        name="convert100"
                        onChange={handleSubjectFormChange}
                        value={subjectForm.convert100}
                        disabled={
                          subjectForm?.subjectCategory !== "arts_pe"
                            ? true
                            : false
                        }
                      />
                    </div>
                    <div className="" style={{ width: "60px" }}>
                      <label className="form-label">EF</label>
                      <input
                        type="text"
                        className="form-control form-control-sm  p-1"
                        name="ef"
                        onChange={handleSubjectFormChange}
                        value={subjectForm.ef}
                        disabled={
                          subjectForm?.subjectCategory !== "arts_pe"
                            ? true
                            : false
                        }
                      />
                    </div>
                    <div
                      className="d-flex align-items-end"
                      style={{ width: "55px" }}
                    >
                      <button
                        className="btn btn-success btn-sm ps-1 pe-1 mb-0 ms-1 me-0"
                        onClick={handleAddSubject}
                      >
                        추가
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* 과목 목록 테이블 */}
              {transferStudentData && transferStudentData.length > 0 && (
                <div className="card">
                  <div className="card-header">
                    <h5 className="mb-0">
                      추가된 과목 목록 ({transferStudentData.length}개)
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <DynamicTable
                        tableConfig={tableConfig}
                        dataStructure="flat"
                        tableStyle="cleanModern"
                        sortable={true}
                        initialSortField="order_num"
                        initialSortDirection="asc"
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="alert alert-warning mt-4">
              <h5>
                해당 학년도와 학기에 학년과 성적유형, 학교명을 먼저 저장해주세요
              </h5>
              <p>
                해당 학년도와 학기에 저장된 전학생 기록이 있어야 과목을 입력할
                수 있습니다.
              </p>
            </div>
          )}
        </>
      ) : (
        <div className="alert alert-info mt-4">
          <h5>학생과 학년도, 학기를 선택해주세요</h5>
          <p>
            학생아이디, 학년도, 학기 단위로 전학생 성적 데이터를 관리할 수
            있습니다.
          </p>
        </div>
      )}
    </div>
  );
}
